import { useState, useEffect, useCallback, useRef } from 'react';
import { signalRService } from '../../services/signalr/SignalRService';
import {
  HubMethods,
  HubEvents,
  EconomySnapshot,
  EconomyInventoryItem,
  EconomyCatalogItem,
  SendGiftResult,
  RespondToGiftResult,
  GiftOfferedEvent,
  EconomyUpdateEvent,
  SocialEventEnvelope,
  PotionThrowBroadcast,
  ThrowTarget,
  ThrowTargetsResult,
} from '../../services/signalr/types';
import { RoomPlayer } from '../../types/user';
import { useUser } from '../../context/UserContext';
import './SocialPlayPanel.css';

interface SocialPlayPanelProps {
  roomId: string;
  players: RoomPlayer[];
}

interface Notice {
  id: string;
  text: string;
  kind: 'info' | 'spark' | 'error';
}

/** An effect potion that just hit me — Cheers mints ✦ for both, Shake-off clears silently. */
interface IncomingSplash {
  throwId: string;
  fromName: string;
  itemName: string;
  icon: string;
  cheersSparks: number;
}

/** A memory bottle's story (P4) — targeted to whoever touched it. */
interface BottleLore {
  bottleId: string;
  text: string;
  prompt: string;
}

const noticeId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * In-room social-economy surface (Alchemist's Cove P1): Sparks ✦ balance, the inventory
 * "satchel" drawer, gift-a-nearby-player flow, and the incoming gift accept/decline toast.
 * Server is authoritative for every number shown; this component only renders snapshots
 * it received (GetMyEconomy / SendGift / RespondToGift results + targeted EconomyUpdate).
 * Declining an offer is SILENT by design — the giver gets no signal of any kind.
 * Touch-first: everything is a large tap target, nothing depends on hover.
 */
export function SocialPlayPanel({ roomId, players }: SocialPlayPanelProps) {
  const { user } = useUser();
  const [economy, setEconomy] = useState<EconomySnapshot | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [giftPickerFor, setGiftPickerFor] = useState<string | null>(null);
  const [throwPickerFor, setThrowPickerFor] = useState<string | null>(null);
  const [throwTargets, setThrowTargets] = useState<ThrowTarget[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [offers, setOffers] = useState<GiftOfferedEvent[]>([]);
  const [splashes, setSplashes] = useState<IncomingSplash[]>([]);
  const [lore, setLore] = useState<BottleLore | null>(null);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [sparkPulse, setSparkPulse] = useState(false);
  const userIdRef = useRef<string | undefined>(user?.id);
  userIdRef.current = user?.id;

  const pushNotice = useCallback((text: string, kind: Notice['kind'] = 'info') => {
    const n = { id: noticeId(), text, kind };
    setNotices(prev => [...prev.slice(-2), n]);
    setTimeout(() => setNotices(prev => prev.filter(x => x.id !== n.id)), 6000);
  }, []);

  const applyBalances = useCallback((sparks?: number, inventory?: EconomyInventoryItem[]) => {
    setEconomy(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      if (typeof sparks === 'number') {
        if (sparks !== prev.sparks) setSparkPulse(true);
        next.sparks = sparks;
      }
      if (inventory) next.inventory = inventory;
      return next;
    });
  }, []);

  // Owned items → Unity (P6): key-gated doors (Duo Key) read this set. Server-authoritative
  // inventory; Unity only uses it for cosmetic gates. UnityGame relays the event as an
  // `ownedItems` bridge message.
  useEffect(() => {
    if (!economy?.ok) return;
    const items = economy.inventory.filter(i => i.qty > 0).map(i => i.itemType);
    window.dispatchEvent(new CustomEvent('flexo-owned-items', { detail: { items } }));
  }, [economy]);

  useEffect(() => {
    if (!sparkPulse) return;
    const t = setTimeout(() => setSparkPulse(false), 900);
    return () => clearTimeout(t);
  }, [sparkPulse]);

  // Snapshot on entering the room (also grants the starter kit server-side on first ever call).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const json = await signalRService.invoke<string>(HubMethods.GetMyEconomy);
        if (cancelled || !json) return;
        const snap = JSON.parse(json) as EconomySnapshot;
        if (snap.ok) setEconomy(snap);
        else console.warn('Economy unavailable:', snap.error);
      } catch (e) {
        // Older server without the economy surface — hide the panel entirely.
        console.warn('Economy not available on this server:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  // Targeted pushes: an offer for me, or my balances changed (e.g. my gift was accepted).
  useEffect(() => {
    const onGiftOffered = (...args: unknown[]) => {
      const offer = args[0] as GiftOfferedEvent;
      if (!offer?.giftId) return;
      setOffers(prev => (prev.some(o => o.giftId === offer.giftId) ? prev : [...prev, offer]));
      // Quietly drop the toast when the offer lapses server-side.
      const ttl = Math.max(5000, new Date(offer.expiresAt).getTime() - Date.now());
      setTimeout(() => setOffers(prev => prev.filter(o => o.giftId !== offer.giftId)), ttl);
    };

    const onEconomyUpdate = (...args: unknown[]) => {
      const update = args[0] as EconomyUpdateEvent;
      if (!update || update.userId !== userIdRef.current) return;
      applyBalances(update.sparks, update.inventory);
      // 'gift_accepted' targets the GIVER (the receiver's own accept feedback comes from the
      // RespondToGift result, reason 'gift_received' — no double toast). Same split for
      // potions: 'potion_cheered' is the THROWER's toast.
      if (update.reason === 'gift_accepted' && (update.sparksDelta ?? 0) > 0 && update.detail) {
        pushNotice(
          `${update.detail.toName ?? 'They'} accepted your ${update.detail.itemName ?? 'gift'} +${update.sparksDelta}✦`,
          'spark',
        );
      } else if (update.reason === 'potion_cheered' && (update.sparksDelta ?? 0) > 0 && update.detail) {
        pushNotice(
          `${update.detail.toName ?? 'They'} cheered your ${update.detail.itemName ?? 'potion'} +${update.sparksDelta}✦`,
          'spark',
        );
      } else if (update.reason === 'duel_won') {
        pushNotice(`🏆 You won the Splash Duel! +${update.sparksDelta ?? 0}✦`, 'spark');
      } else if (update.reason === 'duel_lost') {
        pushNotice(`Splash Duel lost — still +${update.sparksDelta ?? 0}✦ for playing`, 'info');
      } else if (update.reason === 'duel_tie') {
        pushNotice(`Splash Duel: dead heat! +${update.sparksDelta ?? 0}✦ each`, 'spark');
      }
    };

    // Server-validated social events (room-group broadcast). The 3D replay happens in
    // Unity; this handler owns the RECEIVER's consent UI (Cheers / Shake-off) and the
    // cleanup of splash toasts resolved from another tab.
    const onSocialEvent = (...args: unknown[]) => {
      let envelope: SocialEventEnvelope;
      try {
        envelope = JSON.parse(args[0] as string) as SocialEventEnvelope;
      } catch {
        return;
      }
      if (envelope.type === 'potion_throw' && envelope.to === userIdRef.current) {
        let payload: PotionThrowBroadcast;
        try {
          payload = JSON.parse(envelope.payloadJson) as PotionThrowBroadcast;
        } catch {
          return;
        }
        const splash: IncomingSplash = {
          throwId: payload.throwId,
          fromName: envelope.fromName,
          itemName: payload.itemName,
          icon: payload.icon,
          cheersSparks: payload.cheersSparks,
        };
        setSplashes(prev => (prev.some(s => s.throwId === splash.throwId) ? prev : [...prev, splash]));
        const ttl = Math.max(10, payload.cheersWindowSeconds ?? 90) * 1000;
        setTimeout(() => setSplashes(prev => prev.filter(s => s.throwId !== splash.throwId)), ttl);
      } else if (envelope.type === 'potion_cheers' || envelope.type === 'potion_shakeoff') {
        let throwId = '';
        try {
          throwId = (JSON.parse(envelope.payloadJson) as { throwId?: string }).throwId ?? '';
        } catch {
          return;
        }
        if (throwId) setSplashes(prev => prev.filter(s => s.throwId !== throwId));
      } else if (envelope.type === 'puzzle_state') {
        // Shared puzzles (P4): the server narrates progress/solves for the whole room.
        try {
          const ps = JSON.parse(envelope.payloadJson) as {
            puzzleId: string; solved?: boolean; reset?: boolean; note?: string;
            participants?: string[]; traits?: string[]; sparks?: number;
            progress?: number; steps?: number; padsHeld?: number; padsNeeded?: number;
          };
          if (ps.solved) {
            const who = (ps.participants ?? []).join(' & ');
            const traits = ps.traits?.length ? ` — You both chose: ${ps.traits.join(', ')}` : '';
            pushNotice(`✨ ${who} solved it together! +${ps.sparks ?? 0}✦ each${traits}`, 'spark');
          } else if (ps.reset) {
            pushNotice(ps.note ? `Poof! ${ps.note}` : 'Poof! The attempt fizzled — try again', 'info');
          } else if (ps.note) {
            pushNotice(ps.note, 'info');
          } else if ((ps.padsNeeded ?? 0) > 0 && (ps.padsHeld ?? 0) > 0) {
            pushNotice(`${ps.puzzleId.replace(/_/g, ' ')}: ${ps.padsHeld}/${ps.padsNeeded} in place…`, 'info');
          } else if ((ps.steps ?? 0) > 0 && (ps.progress ?? 0) > 0) {
            pushNotice(`${ps.puzzleId.replace(/_/g, ' ')}: step ${ps.progress}/${ps.steps}`, 'info');
          }
        } catch { /* ignore malformed */ }
      } else if (envelope.type === 'puzzle_note' && envelope.to === userIdRef.current) {
        try {
          const pn = JSON.parse(envelope.payloadJson) as { note?: string; item?: string };
          if (pn.note === 'missing_item') {
            pushNotice(`You need ${pn.item ? pn.item.replace(/_/g, '-') : 'an ingredient'} in your satchel`, 'error');
          } else if (pn.note === 'wait_your_turn') {
            pushNotice('Let someone else take the next step', 'error');
          }
        } catch { /* ignore */ }
      } else if (envelope.type === 'bottle_lore' && envelope.to === userIdRef.current) {
        try {
          const bl = JSON.parse(envelope.payloadJson) as BottleLore;
          if (bl?.text) {
            setLore(bl);
            setTimeout(() => setLore(prev => (prev?.bottleId === bl.bottleId ? null : prev)), 16000);
          }
        } catch { /* ignore */ }
      }
    };

    signalRService.on(HubEvents.GiftOffered, onGiftOffered);
    signalRService.on(HubEvents.EconomyUpdate, onEconomyUpdate);
    signalRService.on(HubEvents.SocialEventReceived, onSocialEvent);
    return () => {
      signalRService.off(HubEvents.GiftOffered, onGiftOffered);
      signalRService.off(HubEvents.EconomyUpdate, onEconomyUpdate);
      signalRService.off(HubEvents.SocialEventReceived, onSocialEvent);
    };
  }, [applyBalances, pushNotice]);

  const sendGift = useCallback(async (itemType: string, toPlayer: RoomPlayer) => {
    if (busy) return;
    setBusy(true);
    try {
      const json = await signalRService.invoke<string>(HubMethods.SendGift, roomId, toPlayer.id, itemType);
      const result = JSON.parse(json) as SendGiftResult;
      if (result.ok) {
        applyBalances(undefined, result.inventory);
        setGiftPickerFor(null);
        pushNotice(`Gift offered to ${toPlayer.displayName} 🎁`);
      } else if (result.error === 'cooldown') {
        pushNotice(`Easy does it — you can offer ${toPlayer.displayName} another gift in ${result.retryInSeconds ?? 60}s`, 'error');
      } else if (result.error === 'daily_limit') {
        pushNotice(`You've showered ${toPlayer.displayName} with gifts today — try again tomorrow`, 'error');
      } else if (result.error === 'no_item') {
        pushNotice('That item is no longer in your satchel', 'error');
      } else if (result.error === 'target_not_in_room') {
        pushNotice(`${toPlayer.displayName} seems to have stepped out`, 'error');
      } else {
        pushNotice('The gift could not be sent right now', 'error');
      }
    } catch (e) {
      console.warn('SendGift failed:', e);
      pushNotice('The gift could not be sent right now', 'error');
    } finally {
      setBusy(false);
    }
  }, [roomId, busy, applyBalances, pushNotice]);

  // Server-computed targetability: "nobody"/blocks/auto-mute collapse into allowed=false —
  // those players render greyed-out and are literally untargetable from this UI (and the
  // server re-validates every throw anyway).
  const openThrowPicker = useCallback(async (itemType: string) => {
    if (throwPickerFor === itemType) {
      setThrowPickerFor(null);
      return;
    }
    setThrowTargets(null);
    setThrowPickerFor(itemType);
    setGiftPickerFor(null);
    try {
      const json = await signalRService.invoke<string>(HubMethods.GetThrowTargets, roomId);
      const result = JSON.parse(json) as ThrowTargetsResult;
      setThrowTargets(result.ok ? (result.targets ?? []) : []);
    } catch {
      setThrowTargets([]);
    }
  }, [roomId, throwPickerFor]);

  const throwPotion = useCallback(async (potionType: string, target: ThrowTarget) => {
    if (busy || !target.allowed) return;
    setBusy(true);
    try {
      const envelope = JSON.stringify({
        type: 'potion_throw',
        payloadJson: JSON.stringify({ potionType, targetUserId: target.userId }),
      });
      const json = await signalRService.invoke<string>(HubMethods.SocialEvent, roomId, envelope);
      const result = JSON.parse(json) as SendGiftResult & { throwId?: string };
      if (result.ok) {
        applyBalances(undefined, result.inventory);
        setThrowPickerFor(null);
        pushNotice(`You splashed ${target.displayName}! 🧪`);
      } else if (result.error === 'cooldown') {
        pushNotice(`Give ${target.displayName} a moment — try again in ${result.retryInSeconds ?? 15}s`, 'error');
      } else if (result.error === 'daily_limit') {
        pushNotice(`${target.displayName} has been thoroughly splashed today`, 'error');
      } else if (result.error === 'no_item') {
        pushNotice('That potion is no longer in your satchel', 'error');
      } else {
        pushNotice('The potion fizzled — try someone else', 'error');
      }
    } catch (e) {
      console.warn('potion_throw failed:', e);
      pushNotice('The potion fizzled — try again', 'error');
    } finally {
      setBusy(false);
    }
  }, [roomId, busy, applyBalances, pushNotice]);

  const respondToSplash = useCallback(async (splash: IncomingSplash, cheers: boolean) => {
    setSplashes(prev => prev.filter(s => s.throwId !== splash.throwId));
    try {
      const envelope = JSON.stringify({
        type: cheers ? 'potion_cheers' : 'potion_shakeoff',
        payloadJson: JSON.stringify({ throwId: splash.throwId }),
      });
      const json = await signalRService.invoke<string>(HubMethods.SocialEvent, roomId, envelope);
      const result = JSON.parse(json) as { ok: boolean; sparks?: number };
      if (cheers && result.ok) {
        applyBalances(result.sparks, undefined);
        pushNotice(`Cheers! +${splash.cheersSparks}✦`, 'spark');
      }
      // Shake-off: the effect clears for everyone via the broadcast; nothing else to say.
    } catch (e) {
      console.warn('potion respond failed:', e);
    }
  }, [roomId, applyBalances, pushNotice]);

  const brewPotion = useCallback(async (item: EconomyCatalogItem) => {
    if (busy) return;
    setBusy(true);
    try {
      const json = await signalRService.invoke<string>(HubMethods.BrewPotion, item.type);
      const result = JSON.parse(json) as RespondToGiftResult;
      if (result.ok) {
        applyBalances(result.sparks, result.inventory);
        pushNotice(`Brewed a ${item.name} −${item.brewCost}✦`);
      } else if (result.error === 'insufficient_sparks') {
        pushNotice(`Not enough Sparks — brewing costs ${item.brewCost}✦`, 'error');
      } else {
        pushNotice('The cauldron sputtered — try again', 'error');
      }
    } catch (e) {
      console.warn('BrewPotion failed:', e);
      pushNotice('The cauldron sputtered — try again', 'error');
    } finally {
      setBusy(false);
    }
  }, [busy, applyBalances, pushNotice]);

  const respondToOffer = useCallback(async (offer: GiftOfferedEvent, accept: boolean) => {
    setOffers(prev => prev.filter(o => o.giftId !== offer.giftId));
    try {
      const json = await signalRService.invoke<string>(HubMethods.RespondToGift, offer.giftId, accept);
      const result = JSON.parse(json) as RespondToGiftResult;
      if (accept && result.ok) {
        applyBalances(result.sparks, result.inventory);
        pushNotice(`${offer.itemName} added to your satchel +${offer.receiverSparksOnAccept}✦`, 'spark');
      } else if (accept && result.error === 'expired') {
        pushNotice('That offer had already faded away', 'error');
      }
      // Decline: no feedback needed beyond the toast disappearing — and the giver hears nothing.
    } catch (e) {
      console.warn('RespondToGift failed:', e);
    }
  }, [applyBalances, pushNotice]);

  if (!user || !economy?.ok) return null;

  const catalogOf = (itemType: string): EconomyCatalogItem | undefined =>
    economy.catalog.find(c => c.type === itemType);
  const giftTargets = players.filter(p => p.id !== user.id);

  return (
    <>
      {/* Sparks chip + satchel toggle */}
      <div className="social-play-anchor">
        <button
          className={`social-chip ${sparkPulse ? 'spark-pulse' : ''}`}
          onClick={() => { setDrawerOpen(!drawerOpen); setGiftPickerFor(null); }}
          aria-label="Open satchel"
        >
          <span className="social-chip-bag">🎒</span>
          <span className="social-chip-sparks">✦ {economy.sparks}</span>
        </button>

        {drawerOpen && (
          <div className="satchel-drawer">
            <div className="satchel-header">
              <span>My Satchel</span>
              <span className="satchel-sparks">✦ {economy.sparks} Sparks</span>
            </div>
            <div className="satchel-hint">
              Sparks are minted by doing things together — a gift someone accepts earns you both ✦
            </div>
            <div className="satchel-items">
              {economy.inventory.length === 0 && (
                <div className="satchel-empty">
                  Your satchel is empty. Earn items by playing together — or wait for a gift.
                </div>
              )}
              {economy.inventory.map(item => {
                const cat = catalogOf(item.itemType);
                return (
                  <div key={item.itemType} className="satchel-item">
                    <div className="satchel-item-row">
                      <span className="satchel-item-icon">{cat?.icon ?? '📦'}</span>
                      <span className="satchel-item-name">{cat?.name ?? item.itemType}</span>
                      <span className="satchel-item-qty">×{item.qty}</span>
                      {cat?.potion && giftTargets.length > 0 && (
                        <button
                          className="satchel-throw-btn"
                          disabled={busy}
                          onClick={() => openThrowPicker(item.itemType)}
                        >
                          Splash
                        </button>
                      )}
                      {cat?.giftable !== false && giftTargets.length > 0 && (
                        <button
                          className="satchel-gift-btn"
                          disabled={busy}
                          onClick={() => {
                            setGiftPickerFor(giftPickerFor === item.itemType ? null : item.itemType);
                            setThrowPickerFor(null);
                          }}
                        >
                          Gift
                        </button>
                      )}
                    </div>
                    {cat?.desc && <div className="satchel-item-desc">{cat.desc}</div>}
                    {giftPickerFor === item.itemType && (
                      <div className="gift-picker">
                        <div className="gift-picker-title">
                          Give to… {economy.config ? `(they accept → you +${economy.config.giftGiverSparksOnAccept}✦)` : ''}
                        </div>
                        {giftTargets.map(p => (
                          <button
                            key={p.id}
                            className="gift-picker-target"
                            disabled={busy}
                            onClick={() => sendGift(item.itemType, p)}
                          >
                            {p.displayName}
                          </button>
                        ))}
                      </div>
                    )}
                    {throwPickerFor === item.itemType && (
                      <div className="gift-picker">
                        <div className="gift-picker-title">
                          Splash whom? (they can always shake it off — Cheers earns you both ✦)
                        </div>
                        {throwTargets === null && <div className="gift-picker-title">Checking who's game…</div>}
                        {throwTargets?.length === 0 && <div className="gift-picker-title">No one available right now.</div>}
                        {throwTargets?.map(t => (
                          <button
                            key={t.userId}
                            className={`gift-picker-target ${t.allowed ? '' : 'throw-target-blocked'}`}
                            disabled={busy || !t.allowed}
                            onClick={() => throwPotion(item.itemType, t)}
                          >
                            {t.displayName}
                            {!t.allowed && <span className="throw-target-note"> — not available</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Brewing bench: Sparks → potions (every price is server data) */}
            {economy.catalog.some(c => c.potion && (c.brewCost ?? 0) > 0) && (
              <div className="brew-bench">
                <div className="brew-bench-title">Brewing bench</div>
                {economy.catalog.filter(c => c.potion && (c.brewCost ?? 0) > 0).map(c => (
                  <div key={c.type} className="brew-row">
                    <span className="satchel-item-icon">{c.icon}</span>
                    <span className="satchel-item-name">{c.name}</span>
                    <button
                      className="satchel-gift-btn"
                      disabled={busy || economy.sparks < (c.brewCost ?? 0)}
                      onClick={() => brewPotion(c)}
                    >
                      Brew {c.brewCost}✦
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Transient notices (gift sent / sparks minted / soft errors) */}
        <div className="social-notices">
          {notices.map(n => (
            <div key={n.id} className={`social-notice social-notice-${n.kind}`}>{n.text}</div>
          ))}
        </div>
      </div>

      {/* Incoming gift offers — accept mints ✦ for both; declining is silent and shame-free */}
      <div className="gift-offer-stack">
        {offers.map(offer => (
          <div key={offer.giftId} className="gift-offer-toast">
            <div className="gift-offer-text">
              <span className="gift-offer-icon">{offer.icon || '🎁'}</span>
              <span><strong>{offer.fromName}</strong> offers you a <strong>{offer.itemName}</strong></span>
            </div>
            <div className="gift-offer-actions">
              <button className="gift-accept-btn" onClick={() => respondToOffer(offer, true)}>
                Accept +{offer.receiverSparksOnAccept}✦
              </button>
              <button className="gift-decline-btn" onClick={() => respondToOffer(offer, false)}>
                No thanks
              </button>
            </div>
          </div>
        ))}

        {/* Memory-bottle lore (P4): a private story fragment + a conversation prompt */}
        {lore && (
          <div className="lore-card" onClick={() => setLore(null)}>
            <div className="lore-title">🍾 A memory bottle whispers…</div>
            <div className="lore-text">{lore.text}</div>
            {lore.prompt && <div className="lore-prompt">{lore.prompt}</div>}
          </div>
        )}

        {/* Potion hits on ME — Cheers rewards both, Shake-off clears instantly (always available) */}
        {splashes.map(splash => (
          <div key={splash.throwId} className="gift-offer-toast">
            <div className="gift-offer-text">
              <span className="gift-offer-icon">{splash.icon || '🧪'}</span>
              <span><strong>{splash.fromName}</strong> splashed you with a <strong>{splash.itemName}</strong>!</span>
            </div>
            <div className="gift-offer-actions">
              <button className="gift-accept-btn" onClick={() => respondToSplash(splash, true)}>
                Cheers! +{splash.cheersSparks}✦
              </button>
              <button className="gift-decline-btn" onClick={() => respondToSplash(splash, false)}>
                Shake it off
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
